import { useState, useEffect } from 'react'
import { AccountCredentialForm } from '@/components/integrations/AccountCredentialForm'
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { toast } from 'sonner'
import { INTEGRATIONS } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import * as z from 'zod'
import type { YardiVoyagerMetadata } from '@/lib/api/integrations/yardi-voyager.client'

const parseYardiVoyagerUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url)
    const match = urlObj.hostname.match(/^([^.]+)\.yardione\.com$/)
    return match ? match[1] : null
  } catch {
    return null
  }
}

const yardiVoyagerAdditionalSchema = z.object({
  yardiVoyagerUrl: z
    .string()
    .min(1, 'Yardi Voyager URL is required')
    .refine(
      url => {
        try {
          new URL(url)
          return true
        } catch {
          return false
        }
      },
      { message: 'Please enter a valid URL' },
    )
    .refine(url => parseYardiVoyagerUrl(url) !== null, {
      message: 'URL must be in format: https://yourcompanyid.yardione.com',
    }),
  defaultProperty: z.string().optional(),
  laborEmployee: z.string().optional(),
})

export interface YardiVoyagerConnectFormProps {
  isEditMode?: boolean
  onConnected: () => void
}

export function YardiVoyagerConnectForm({
  isEditMode = false,
  onConnected,
}: YardiVoyagerConnectFormProps) {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [existingCredential, setExistingCredential] = useState<{
    accountIdentifier: string
  } | null>(null)
  const [yardiVoyagerUrl, setYardiVoyagerUrl] = useState<string>('')
  const [defaultProperty, setDefaultProperty] = useState<string>('')
  const [laborEmployee, setLaborEmployee] = useState<string>('')
  const [isLoading, setIsLoading] = useState(isEditMode)

  useEffect(() => {
    if (!isEditMode || !currentCompany?.id) {
      setIsLoading(false)
      return
    }

    const fetchStatus = async () => {
      try {
        setIsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getAllAdminIntegrationsStatus(
              currentCompany.id,
            )
          : await integrationsClient.getAllIntegrationsStatus()
        const yardiVoyager = integrations.find(
          i =>
            i.type === INTEGRATIONS.YARDI_VOYAGER.TYPE ||
            i.name === INTEGRATIONS.YARDI_VOYAGER.NAME,
        )
        if (yardiVoyager?.connected && yardiVoyager.accountId) {
          setExistingCredential({ accountIdentifier: yardiVoyager.accountId })
          if (yardiVoyager.metadata?.configId) {
            setYardiVoyagerUrl(
              `https://${yardiVoyager.metadata.configId}.yardione.com`,
            )
          }
          if (yardiVoyager.metadata?.defaultProperty) {
            setDefaultProperty(yardiVoyager.metadata.defaultProperty as string)
          }
          if (yardiVoyager.metadata?.laborEmployee) {
            setLaborEmployee(yardiVoyager.metadata.laborEmployee as string)
          }
        }
      } catch (error) {
        console.error('Error fetching integration status:', error)
        toast.error('Failed to load existing credentials')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStatus()
  }, [isEditMode, currentCompany?.id, isSuperAdmin])

  const handleSubmit = async (data: {
    accountIdentifier: string
    password: string
    yardiVoyagerUrl?: string
    defaultProperty?: string
    laborEmployee?: string
  }) => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }

    const configId = data.yardiVoyagerUrl
      ? parseYardiVoyagerUrl(data.yardiVoyagerUrl)
      : null

    if (!configId) {
      toast.error('Invalid Yardi Voyager URL format')
      return
    }

    const metadata: YardiVoyagerMetadata = {
      configId,
      ...(data.defaultProperty && { defaultProperty: data.defaultProperty }),
      ...(data.laborEmployee && { laborEmployee: data.laborEmployee }),
    }

    try {
      if (isEditMode) {
        if (isSuperAdmin) {
          await integrationsClient.yardiVoyager.updateAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.yardiVoyager.updateCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Yardi Voyager credentials updated successfully!')
      } else {
        if (isSuperAdmin) {
          await integrationsClient.yardiVoyager.createAdminCredentials(
            currentCompany.id,
            data.accountIdentifier,
            data.password,
            metadata,
          )
        } else {
          await integrationsClient.yardiVoyager.createCredentials(
            data.accountIdentifier,
            data.password,
            metadata,
          )
        }
        toast.success('Yardi Voyager integration connected successfully!')
      }
      onConnected()
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'connecting'} Yardi Voyager:`,
        error,
      )
      toast.error(
        `Failed to ${isEditMode ? 'update' : 'connect'} Yardi Voyager integration`,
      )
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
        <div className="h-20 bg-secondary/50 animate-pulse rounded" />
      </div>
    )
  }

  return (
    <AccountCredentialForm
      onSubmit={handleSubmit}
      accountIdentifierLabel="Email"
      accountIdentifierPlaceholder="your-yardi-email@example.com"
      passwordLabel="Password"
      passwordPlaceholder="Enter your password"
      submitButtonText={isEditMode ? 'Update' : 'Connect'}
      initialAccountIdentifier={existingCredential?.accountIdentifier}
      additionalSchema={yardiVoyagerAdditionalSchema}
      initialValues={{ yardiVoyagerUrl, defaultProperty, laborEmployee }}
      confirmationText={
        isEditMode
          ? 'Update your credentials for Yardi Voyager'
          : 'Connect your credentials for Yardi Voyager'
      }
    >
      {({ form, isSubmitting }) => (
        <>
          <FormField
            control={form.control}
            name="yardiVoyagerUrl"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Yardi Voyager URL</FormLabel>
                <FormControl>
                  <Input
                    placeholder="https://yourcompanyid.yardione.com"
                    disabled={isSubmitting}
                    value={field.value as string}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                </FormControl>
                <FormDescription>
                  Your Yardi Voyager portal URL (e.g.,
                  https://yourcompanyid.yardione.com)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="defaultProperty"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Default property (optional)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="121 Main St, Anytown, CA"
                    disabled={isSubmitting}
                    value={field.value as string}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                </FormControl>
                <FormDescription>
                  The default property address to use for the integration
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="laborEmployee"
            render={({ field }) => (
              <FormItem className="max-w-md">
                <FormLabel>Default labor employee (optional)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g. John Smith or 12345"
                    disabled={isSubmitting}
                    value={field.value as string}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                </FormControl>
                <FormDescription>
                  Fallback labor employee (name or code) used when a handoff
                  rule has no on-call assignee
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </AccountCredentialForm>
  )
}
